import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Camera, X, Loader2, ImagePlus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import imageCompression from 'browser-image-compression';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'https://devisfast-backend.onrender.com/api';

const QuotePhotos = ({ quoteId, photos = [], token, onPhotosChange }) => {
  const [uploading, setUploading] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset error message
    setErrorMessage(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      const msg = 'Format non supporté. Utilisez une image JPEG ou PNG.';
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      const msg = 'Image trop volumineuse. Maximum 10MB.';
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    // Check max photos
    if (photos.length >= 3) {
      const msg = 'Maximum 3 photos autorisées.';
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    setUploading(true);

    try {
      // Compress image with EXIF orientation correction (critical for iOS Safari)
      // exifOrientation: -1 forces the library to auto-correct rotation from EXIF metadata
      const compressionOptions = {
        maxSizeMB: 0.3,            // Max 300KB after compression (as per spec)
        maxWidthOrHeight: 1200,   // Max dimension
        useWebWorker: true,
        fileType: 'image/jpeg',
        initialQuality: 0.8,
        exifOrientation: -1       // Auto-correct EXIF rotation (fixes iOS photos)
      };

      let compressedFile;
      try {
        compressedFile = await imageCompression(file, compressionOptions);
      } catch (compressionError) {
        console.error('Compression error:', compressionError);
        const msg = 'Erreur lors du chargement de la photo';
        setErrorMessage(msg);
        toast.error(msg);
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Convert to base64
      let base64;
      try {
        base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Erreur de lecture du fichier'));
          reader.readAsDataURL(compressedFile);
        });
      } catch (readError) {
        console.error('FileReader error:', readError);
        const msg = 'Erreur lors du chargement de la photo';
        setErrorMessage(msg);
        toast.error(msg);
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Upload to backend
      const response = await axios.post(
        `${API}/quotes/${quoteId}/photos`,
        {
          filename: file.name,
          data_base64: base64,
          media_type: 'image/jpeg'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Only call onPhotosChange AFTER successful upload (status 200/201)
      if (response.status === 200 || response.status === 201) {
        toast.success('Photo ajoutée');
        onPhotosChange?.();
      } else {
        throw new Error('Réponse inattendue du serveur');
      }

    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur lors de l\'upload';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (index) => {
    setDeletingIndex(index);
    setErrorMessage(null);
    
    try {
      await axios.delete(
        `${API}/quotes/${quoteId}/photos/${index}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Photo supprimée');
      onPhotosChange?.();
    } catch (error) {
      console.error('Delete error:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la suppression';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setDeletingIndex(null);
    }
  };

  return (
    <Card className="glass-card border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Camera className="w-5 h-5 text-primary" />
          Photos chantier
          <span className="text-sm font-normal text-muted-foreground">
            ({photos.length}/3)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Error Message */}
        {errorMessage && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-destructive">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Photo Grid */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {photos.map((photo, index) => (
            <div 
              key={index} 
              className="relative aspect-square bg-muted rounded-lg overflow-hidden group"
            >
              <img
                src={photo.data_base64?.startsWith('data:') 
                  ? photo.data_base64 
                  : `data:image/jpeg;base64,${photo.data_base64}`}
                alt={photo.filename || `Photo ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => handleDelete(index)}
                disabled={deletingIndex === index}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 opacity-100 md:opacity-0 transition-opacity hover:bg-red-600"
                data-testid={`delete-photo-${index}`}
              >
                {deletingIndex === index ? (
                  <Loader2 className="w-3 h-3 text-white animate-spin" />
                ) : (
                  <X className="w-3 h-3 text-white" />
                )}
              </button>
            </div>
          ))}
          
          {/* Empty slots */}
          {Array.from({ length: 3 - photos.length }).map((_, index) => (
            <div 
              key={`empty-${index}`}
              className="aspect-square bg-muted/30 rounded-lg border-2 border-dashed border-muted flex items-center justify-center"
            >
              <ImagePlus className="w-6 h-6 text-muted-foreground/50" />
            </div>
          ))}
        </div>

        {/* Upload Button */}
        {photos.length < 3 && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="photo-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="w-full"
              data-testid="add-photo-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Compression et upload...
                </>
              ) : (
                <>
                  <Camera className="w-4 h-4 mr-2" />
                  Ajouter une photo
                </>
              )}
            </Button>
          </>
        )}

        {photos.length > 0 && (
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Les photos seront incluses dans le PDF
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default QuotePhotos;
